import excel from 'exceljs';
import moment from 'moment';
import {
  convertGMT8date,
  generateDynamicExcelColumnKeys,
  generateDynamicExcelHeaders,
  getExcelAlpha,
  getValueForKey,
} from '../../ui/utils';
import { DynamicObjectInterface } from '../type';
import { axiosPost } from './axiosUtils';

type Section = {
  label: string;
  color: string;
  colSpan: number;
};

const generateReport = {
  async teamListReport(name: string, campaignDetail: DynamicObjectInterface) {
    const workbook = new excel.Workbook();

    const worksheet = workbook.addWorksheet('Teams List');

    worksheet.mergeCells('A1', 'C1');
    worksheet.getCell('A1').value = 'Report - Teams List';
    worksheet.getCell('A1').font = { bold: true };

    worksheet.getCell('A2').value = 'Campaign';
    worksheet.getCell('A2').font = { bold: true };

    worksheet.mergeCells('B2', 'F2');
    worksheet.getCell('B2').value = `${campaignDetail.campaignId} - ${campaignDetail.campaignName}`;
    worksheet.getCell('B2').font = { color: { argb: 'FF0000' } };

    worksheet.mergeCells('B3', 'D3');
    worksheet.getCell('A3').value = 'Period';
    worksheet.getCell('A3').font = { bold: true };
    worksheet.getCell('B3').value = `${moment(campaignDetail.campaignStart).format('DD MMM YYYY')} ~ ${moment(
      campaignDetail.campaignEnd,
    ).format('DD MMM YYYY')}`;

    // Column headers
    worksheet.getRow(4).values = [
      'No.',
      'Team Name',
      'Description',
      'Team Leader',
      'Team Start Date',
      'Team End Date',
      'New ABO Count',
      'Team Achieve',
      'Team Winning',
    ];
    worksheet.columns = [
      { key: 'no' },
      { key: 'teamName' },
      { key: 'description' },
      { key: 'teamLeader' },
      { key: 'teamStartDate' },
      { key: 'teamEndDate' },
      { key: 'newAboCount' },
      { key: 'teamAchieved' },
      { key: 'teamWinning' },
    ];
    worksheet.getRow(4).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FCE4D6' } };
    worksheet.getRow(4).alignment = { vertical: 'bottom', horizontal: 'center' };

    let result = await axiosPost(`report/?name=${name}`, { campaignId: campaignDetail.campaignId });

    if (result.status === 200) {
      result.data.data.forEach(function (item: DynamicObjectInterface, index: string) {
        worksheet.addRow({
          no: index + 1,
          teamName: item.teamName,
          description: item.description,
          teamLeader: item.teamLeader,
          totalCoach: item.totalCoach,
          teamStartDate: item.teamStartDate,
          teamEndDate: item.teamStartDate,
          teamAchieved: item.isGroupAchieved,
          teamWinning: item.newABO,
        });
      });
    }

    return workbook;
  },
  async teamDetailReport(name: string, campaignDetail: DynamicObjectInterface) {
    const workbook = new excel.Workbook();

    const worksheet = workbook.addWorksheet('Report - Teams Details');
    let maxRewardsLength = 1;

    worksheet.getCell('A1').value = `Report - Teams Details`;
    worksheet.getCell('A2').value = `Campaign`;
    worksheet.getCell('B2').value = `${campaignDetail.campaignId} - ${campaignDetail.campaignName}`;
    worksheet.getCell('B2').font = { color: { argb: 'FF0000' } };

    worksheet.getCell('A1').font = { bold: true };
    worksheet.getCell('A2').font = { bold: true };

    let res = await axiosPost(`report/?name=${name}`, { campaignId: campaignDetail.campaignId });
    const result: DynamicObjectInterface[] = Object.values(res?.data?.data).sort(
      (a: DynamicObjectInterface, b: DynamicObjectInterface) => {
        const dateComparison =
          (new Date(a.team.teamStartDate) as Date).getTime() - (new Date(b.team.teamStartDate) as Date).getTime();
        if (dateComparison !== 0) {
          return dateComparison;
        }
        return a.team.teamName.localeCompare(b.team.teamName);
      },
    );

    const configJson = result[0]?.campaign?.configJson;
    const subChallenges: DynamicObjectInterface[] = configJson?.activities?.subChallenges ?? [];

    // Assessment indicators. For MULTI campaigns each participant's sub-challenge can
    // have a different indicator set, so the report columns are the UNION of all
    // sub-challenges' indicators (deduped by subKpiCode). Each row fills only the
    // indicators present in its own otherIndicators (others stay blank). Non-MULTI
    // keeps the single base participant.rules[4] list.
    let subKpiCode: DynamicObjectInterface[];
    if (configJson?.campaignMode === 'MULTI' && subChallenges.length > 0) {
      const seen = new Set<string>();
      subKpiCode = subChallenges
        .flatMap((sc: DynamicObjectInterface) => sc?.rules?.[4]?.subKpiCode ?? [])
        .filter((kpi: DynamicObjectInterface) => {
          if (!kpi?.subKpiCode || seen.has(kpi.subKpiCode)) return false;
          seen.add(kpi.subKpiCode);
          return true;
        });
    } else {
      subKpiCode = configJson?.activities?.participant?.rules?.[4]?.subKpiCode ?? [];
    }

    // Union of configured check-in days across all sub-challenges in this campaign,
    // so the report has one column per distinct check-in day (sorted ascending).
    const checkInDays: number[] = Array.from(
      new Set(
        subChallenges
          .flatMap((sc: DynamicObjectInterface) => sc?.checkIn?.days ?? [])
          .map((d: number | string) => Number(d))
          .filter((d: number) => !isNaN(d) && d > 0),
      ),
    ).sort((a, b) => a - b);

    // set up headers and column lists
    let headers = generateDynamicExcelHeaders(subKpiCode, checkInDays);
    let columns = generateDynamicExcelColumnKeys(subKpiCode, checkInDays);

    // columns sections list. First group gained 2 columns (Sub-Challenge, Age Group)
    // vs the legacy 23. Test Device sits as its own 1-col group between Product
    // Purchase and Pre-assessment.
    const sections: Section[] = [
      { label: '', color: 'E2EFDA', colSpan: 25 }, // hardcoded colspan based on column content
      { label: 'Product Purchase', color: 'D9E1F2', colSpan: 3 }, // hardcoded colspan based on column content
      { label: 'Test Device', color: 'FFE699', colSpan: 1 },
      { label: 'Pre-assessment', color: 'BDD7EE', colSpan: subKpiCode.length + 3 },
      { label: 'Post-assessment', color: 'C6E0B4', colSpan: subKpiCode.length + 3 },
      // Results = per-indicator diff columns + Individual Completed + Achieved
      // Individual + Achieved Team + Task Completed (4 fixed columns).
      { label: 'Results', color: 'D9E1F2', colSpan: subKpiCode.length + 4 },
      ...(checkInDays.length > 0
        ? [{ label: 'Check-In', color: 'FCE4D6', colSpan: checkInDays.length } as Section]
        : []),
    ];

    // hold the current column position
    let currentCol = 1;

    // maps through every sections
    sections.forEach(section => {
      if (section.label === '') {
        currentCol += section.colSpan;
        return;
      }

      const startCol = getExcelAlpha(currentCol);
      const endCol = getExcelAlpha(currentCol + section.colSpan - 1);
      const cellRef = `${startCol}3`;

      worksheet.mergeCells(`${startCol}3:${endCol}3`);
      worksheet.getCell(cellRef).value = section.label;
      worksheet.getCell(cellRef).alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getCell(cellRef).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: section.color },
      };

      currentCol += section.colSpan;
    });

    for (let i = 0; i < result.length; i++) {
      const item = result[i];
      if (item?.rewards.length > maxRewardsLength) {
        maxRewardsLength = item?.rewards.length;
      }
    }

    // Set the top-level reward headers (Row 3) and sub-headers (Row 4)
    const rowRewardsHeader = worksheet.getRow(3); // Top group headers
    const rowSubHeaders = worksheet.getRow(4); // Sub-headers

    for (let i = 1; i <= maxRewardsLength; i++) {
      const colStart = currentCol + (i - 1) * 3;
      const colEnd = colStart + 2;

      const rewardLabel = maxRewardsLength === 1 ? 'Reward' : `Reward ${i}`;

      worksheet.mergeCells(3, colStart, 3, colEnd);
      rowRewardsHeader.getCell(colStart).value = rewardLabel;
      rowRewardsHeader.getCell(colStart).alignment = { horizontal: 'center', vertical: 'middle' };
      rowRewardsHeader.getCell(colStart).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'BF8F00' },
      };

      // Sub-headers
      rowSubHeaders.getCell(colStart).value = 'Reward Name';
      rowSubHeaders.getCell(colStart + 1).value = 'Reward Status';
      rowSubHeaders.getCell(colStart + 2).value = 'Reward Last Updated';

      rowSubHeaders.getCell(colStart).alignment =
        rowSubHeaders.getCell(colStart + 1).alignment =
        rowSubHeaders.getCell(colStart + 2).alignment =
          { horizontal: 'center' };

      // Push to columns
      columns.push({ key: 'rewardName' + i });
      columns.push({ key: 'rewardStatus' + i });
      columns.push({ key: 'rewardModifiedDate' + i });
    }

    headers.forEach((header, index) => {
      worksheet.getRow(4).getCell(index + 1).value = header;
    });
    worksheet.columns = columns;

    const rewardFillStart = currentCol;
    const totalRewardCols = maxRewardsLength * 3; // each reward uses 3 columns

    for (let i = 0; i < totalRewardCols; i++) {
      const cell = worksheet.getRow(4).getCell(rewardFillStart + i);
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'BF8F00' }, // Rewards fill color
      };
      cell.alignment = { horizontal: 'center', vertical: 'middle' };
    }
    // hold the current column position
    currentCol = 1;

    // maps through every sections, for row 4 header background color
    sections.forEach(section => {
      for (let i = 0; i < section.colSpan; i++) {
        const col = getExcelAlpha(currentCol);
        const cellRef = `${col}4`;

        worksheet.getCell(cellRef).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: section.color },
        };

        currentCol++;
      }
    });

    for (let i = 0; i < result?.length; i++) {
      const item = result[i];
      let row: Record<string, DynamicObjectInterface> = {};

      for (const col of columns) {
        row[col.key] = getValueForKey(col.key, item);
      }

      for (let i = 0; i < item?.rewards.length; i++) {
        let valueArray = item?.rewards[i];
        let data = {
          [`rewardName${i + 1}`]: valueArray?.rewardName,
          [`rewardStatus${i + 1}`]: valueArray.rewardStatus
            ? valueArray.rewardStatus.charAt(0).toUpperCase() + valueArray.rewardStatus.slice(1)
            : '',
          [`rewardModifiedDate${i + 1}`]: valueArray?.modifiedDate
            ? moment(convertGMT8date(valueArray?.modifiedDate)).format('DD-MM-YYYY')
            : '',
        };
        row = { ...row, ...data };
      }

      worksheet.addRow(row);
    }

    return workbook;
  },
  async coachGroupReport(name: string, campaignDetail: DynamicObjectInterface) {
    const workbook = new excel.Workbook();
    const worksheet = workbook.addWorksheet(`Team Leader List`);

    worksheet.mergeCells('A1', 'C1');
    worksheet.getCell('A1').value = 'Report - Team Leader List';
    worksheet.getCell('A1').font = { bold: true };

    worksheet.getCell('A2').value = 'Campaign';
    worksheet.getCell('A2').font = { bold: true };
    worksheet.mergeCells('B2', 'D2');
    worksheet.getCell('B2').value = `${campaignDetail.campaignId} - ${campaignDetail.campaignName}`;
    worksheet.getCell('B2').font = { color: { argb: 'FF0000' } };

    let maxRewardsLength = 1;
    const result = await axiosPost(`report/?name=${name}`, { campaignId: campaignDetail.campaignId });

    const headers = [
      'No.',
      'Team Name',
      'Sub-Challenge',
      'Team Leader ID',
      'Name',
      'ABO ID',
      'Contact',
      'Email',
      'Address',
      'Current Pin',
      'Period',
      'Number of Teams - Total',
      'Number of Teams - Achieved Team Winning Criteria',
      'Number of New ABOs',
    ];

    const columns = [
      { key: 'no', width: 15 },
      { key: 'teamName', width: 25 },
      { key: 'subChallengeName', width: 25 },
      { key: 'coachId', width: 20 },
      { key: 'name', width: 25 },
      { key: 'aboId', width: 20 },
      { key: 'phone', width: 20 },
      { key: 'email', width: 30 },
      { key: 'address', width: 40 },
      { key: 'pin', width: 25 },
      { key: 'period', width: 30 },
      { key: 'totalTeams', width: 25 },
      { key: 'totalTeamAchieved', width: 25 },
      { key: 'numOfTotalNewAbo', width: 30 },
    ];

    const listRow: DynamicObjectInterface[] = [];

    if (result.status === 200) {
      result.data.data.forEach((item: DynamicObjectInterface, index: number) => {
        const row: DynamicObjectInterface = {
          no: index + 1,
          teamName: item.teamName,
          subChallengeName: item.subChallengeName,
          coachId: item.coachId,
          name: item.name,
          aboId: item.aboId,
          phone: item.phone,
          email: item.email,
          address: item.address,
          pin: item.pin,
          period: `${moment(item.enrollmentDate).format('MMM YYYY')} - ${moment(item.endDate).format('MMM YYYY')}`,
          totalTeams: item.totalTeams,
          totalTeamAchieved: item.totalTeamAchieved,
          numOfTotalNewAbo: item.numOfTotalNewAbo,
        };

        const rewards = item?.rewards || [];
        if (rewards.length > maxRewardsLength) maxRewardsLength = rewards.length;

        for (let i = 0; i < rewards.length; i++) {
          const reward = rewards[i];
          row[`rewardName${i + 1}`] = reward?.rewardName || '';
          row[`rewardStatus${i + 1}`] = reward?.rewardStatus
            ? reward.rewardStatus.charAt(0).toUpperCase() + reward.rewardStatus.slice(1)
            : '';
          row[`rewardModifiedDate${i + 1}`] = reward?.modifiedDate
            ? moment(convertGMT8date(reward.modifiedDate)).format('DD-MM-YYYY')
            : '';
        }

        listRow.push(row);
      });

      // Rewards begin right after the 14 fixed columns (No. .. Number of New ABOs).
      const startCol = 15;
      const totalRewardCols = maxRewardsLength * 3;

      const rowRewardsHeader = worksheet.getRow(3);
      const rowSubHeaders = worksheet.getRow(4);

      for (let i = 0; i < maxRewardsLength; i++) {
        const colStart = startCol + i * 3;
        const colEnd = colStart + 2;

        // Merge and style top-level "Rewards X"
        worksheet.mergeCells(3, colStart, 3, colEnd);
        const cell = rowRewardsHeader.getCell(colStart);
        cell.value = maxRewardsLength === 1 ? 'Reward' : `Reward ${i}`;
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FCE4D6' },
        };

        // Sub-headers
        rowSubHeaders.getCell(colStart).value = 'Reward Name';
        rowSubHeaders.getCell(colStart + 1).value = 'Reward Status';
        rowSubHeaders.getCell(colStart + 2).value = 'Reward Last Updated';

        rowSubHeaders.getCell(colStart).alignment =
          rowSubHeaders.getCell(colStart + 1).alignment =
          rowSubHeaders.getCell(colStart + 2).alignment =
            { horizontal: 'center', vertical: 'middle' };

        columns.push({ key: `rewardName${i + 1}`, width: 30 });
        columns.push({ key: `rewardStatus${i + 1}`, width: 30 });
        columns.push({ key: `rewardModifiedDate${i + 1}`, width: 30 });
      }
      headers.forEach((header, index) => {
        worksheet.getRow(4).getCell(index + 1).value = header;
      });

      worksheet.columns = columns;
      worksheet.getRow(4).alignment = { vertical: 'bottom', horizontal: 'center' };

      for (let i = startCol; i < startCol + totalRewardCols; i++) {
        worksheet.getRow(4).getCell(i).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FCE4D6' },
        };
      }

      for (let i = 1; i <= 14; i++) {
        worksheet.getRow(4).getCell(i).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'BDD7EE' },
        };
      }

      listRow.forEach(row => worksheet.addRow(row));
    }

    return workbook;
  },

  async inventoryReport(name: string, campaignDetail: DynamicObjectInterface) {
    const workbook = new excel.Workbook();
    const worksheet = workbook.addWorksheet(`Inventory List`);

    worksheet.mergeCells('A1', 'C1');
    worksheet.getCell('A1').value = 'Report - Inventory List';
    worksheet.getCell('A1').font = { bold: true };

    worksheet.getCell('A2').value = 'Campaign';
    worksheet.getCell('A2').font = { bold: true };
    worksheet.mergeCells('B2', 'D2');
    worksheet.getCell('B2').value = `${campaignDetail.campaignId} - ${campaignDetail.campaignName}`;
    worksheet.getCell('B2').font = { color: { argb: 'FF0000' } };

    let maxRewardsLength = 1;
    const result = await axiosPost(`report/?name=${name}`, { campaignId: campaignDetail.campaignId });

    const headers = [
      'No.',
      'Campaign ID',
      'Location Code',
      'Location Name',
      'Reward Code',
      'Reward Name',
      'Available Stock',
      'Redeemed Stock',
      'Status',
    ];

    const columns = [
      { key: 'no', width: 15 },
      { key: 'campaignId', width: 20 },
      { key: 'locationCode', width: 20 },
      { key: 'locationName', width: 30 },
      { key: 'rewardCode', width: 25 },
      { key: 'rewardName', width: 30 },
      { key: 'availableStock', width: 20 },
      { key: 'redeemedStock', width: 20 },
      { key: 'status', width: 20 },
    ];

    const listRow: DynamicObjectInterface[] = [];

    if (result.status === 200) {
      result.data.data.forEach((item: DynamicObjectInterface, index: number) => {
        const row: DynamicObjectInterface = {
          no: index + 1,
          campaignId: item.campaignId,
          locationCode: item.locationCode,
          locationName: item.locationName,
          rewardCode: item.rewardCode,
          rewardName: item.rewardName,
          availableStock: item.availableStock,
          redeemedStock: item.redeemedStock,
          status: item.status,
        };

        listRow.push(row);
      });

      headers.forEach((header, index) => {
        worksheet.getRow(4).getCell(index + 1).value = header;
      });

      worksheet.columns = columns;
      worksheet.getRow(4).alignment = { vertical: 'bottom', horizontal: 'center' };

      for (let i = 1; i <= 9; i++) {
        worksheet.getRow(4).getCell(i).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'BDD7EE' },
        };
      }

      listRow.forEach(row => worksheet.addRow(row));
    }

    return workbook;
  },

  async promoRedemptionReport(name: string, campaignDetail: DynamicObjectInterface) {
    const workbook = new excel.Workbook();
    const worksheet = workbook.addWorksheet(`Redemption List`);

    worksheet.mergeCells('A1', 'C1');
    worksheet.getCell('A1').value = 'Report - Promotion Redemption List';
    worksheet.getCell('A1').font = { bold: true };

    worksheet.getCell('A2').value = 'Campaign';
    worksheet.getCell('A2').font = { bold: true };
    worksheet.mergeCells('B2', 'D2');
    worksheet.getCell('B2').value = `${campaignDetail.campaignId} - ${campaignDetail.campaignName}`;
    worksheet.getCell('B2').font = { color: { argb: 'FF0000' } };

    let maxRewardsLength = 1;
    const result = await axiosPost(`report/?name=${name}`, { campaignId: campaignDetail.campaignId });

    const headers = [
      'No.',
      'Campaign ID',
      'Order Number',
      'Ordering ABO',
      'Ordering ABO Name',
      'Volume To ABO',
      'Volume To ABO Name',
      'Location Code',
      'Location Name',
      'Channel',
      "Entitled Ticket",
      "Reward Code",
      "Reward Name",
      "Reward Status",
      "Claimed By",
      "Claimed Date"
      
    ];

    const columns = [
      { key: 'no', width: 15 },
      { key: 'campaignId', width: 20 },
      { key: 'orderNumber', width: 25 },
      { key: 'orderAboId', width: 20 },
      { key: 'orderAboName', width: 35 },
      { key: 'volumeAboId', width: 20 },
      { key: 'volumeAboName', width: 35 },
      { key: 'locationCode', width: 15 },
      { key: 'locationName', width: 30 },
      { key: 'channel', width: 15 },
      { key: 'chances', width: 15 },
      { key: 'rewardCode', width: 20 },
      { key: 'rewardName', width: 20 },
      { key: 'status', width: 15 },
      { key: 'player', width: 20 },
      { key: 'createdDate', width: 20 },
    ];

    const listRow: DynamicObjectInterface[] = [];

    if (result.status === 200) {
      result.data.data.forEach((item: DynamicObjectInterface, index: number) => {
        const row: DynamicObjectInterface = {
          no: index + 1,
          campaignId: item.campaignId,
          name: item.orderNumber,
          orderNumber: item.orderNumber,
          orderAboId: item.orderAboId,
          orderAboName: item.orderAboName,
          volumeAboId: item.volumeAboId,
          volumeAboName: item.volumeAboName,
          locationCode: item.locationCode,
          locationName: item.locationName,
          channel: item.channel,
          chances: item.chances,
          rewardCode: item.rewardCode,
          rewardName: item.rewardName,
          status: item.status,
          player: item.player,
          createdDate: item.createdDate,
        };

        listRow.push(row);
      });

      
      headers.forEach((header, index) => {
        worksheet.getRow(4).getCell(index + 1).value = header;
      });

      worksheet.columns = columns;
      worksheet.getRow(4).alignment = { vertical: 'bottom', horizontal: 'center' };

      for (let i = 1; i <= 16; i++) {
        worksheet.getRow(4).getCell(i).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'BDD7EE' },
        };
      }

      listRow.forEach(row => worksheet.addRow(row));
    }

    return workbook;
  },

  // async promoRedemptionReport(name: string, campaignDetail: DynamicObjectInterface) {
  //   const workbook = new excel.Workbook();
  //   const worksheet = workbook.addWorksheet(`Redemption List`);

  //   worksheet.mergeCells('A1', 'C1');
  //   worksheet.getCell('A1').value = 'Report - Promotion Redemption List';
  //   worksheet.getCell('A1').font = { bold: true };

  //   worksheet.getCell('A2').value = 'Campaign';
  //   worksheet.getCell('A2').font = { bold: true };
  //   worksheet.mergeCells('B2', 'D2');
  //   worksheet.getCell('B2').value = `${campaignDetail.campaignId} - ${campaignDetail.campaignName}`;
  //   worksheet.getCell('B2').font = { color: { argb: 'FF0000' } };

  //   let maxRewardsLength = 1;
  //   const result = await axiosPost(`report/?name=${name}`, { campaignId: campaignDetail.campaignId });

  //   const headers = [
  //     'No.',
  //     'Campaign ID',
  //     'Order Number',
  //     'Ordering ABO',
  //     'Ordering ABO Name',
  //     'Volume To ABO',
  //     'Volume To ABO Name',
  //     'Location Code',
  //     'Location Name',
  //     'Channel',
  //     "Entitled Ticket",
  //     "Reward Status",
  //   ];

  //   const columns = [
  //     { key: 'no', width: 15 },
  //     { key: 'campaignId', width: 20 },
  //     { key: 'orderNumber', width: 25 },
  //     { key: 'orderAboId', width: 20 },
  //     { key: 'orderAboName', width: 35 },
  //     { key: 'volumeAboId', width: 20 },
  //     { key: 'volumeAboName', width: 35 },
  //     { key: 'locationCode', width: 25 },
  //     { key: 'locationName', width: 30 },
  //     { key: 'channel', width: 20 },
  //     { key: 'chances', width: 20 },
  //     { key: 'status', width: 25 },
  //   ];

  //   const listRow: DynamicObjectInterface[] = [];

  //   if (result.status === 200) {
  //     result.data.data.forEach((item: DynamicObjectInterface, index: number) => {
  //       const row: DynamicObjectInterface = {
  //         no: index + 1,
  //         campaignId: item.campaignId,
  //         name: item.orderNumber,
  //         orderNumber: item.orderNumber,
  //         orderAboId: item.orderAboId,
  //         orderAboName: item.orderAboName,
  //         volumeAboId: item.volumeAboId,
  //         volumeAboName: item.volumeAboName,
  //         locationCode: item.locationCode,
  //         locationName: item.locationName,
  //         channel: item.channel,
  //         chances: item.chances,
  //         status: item.status,
  //       };

  //       const rewards = item?.rewards || [];
  //       if (rewards.length > maxRewardsLength) maxRewardsLength = rewards.length;

  //       for (let i = 0; i < rewards.length; i++) {
  //         const reward = rewards[i];
  //         row[`rewardCode${i + 1}`] = reward?.rewardCode || '';
  //         row[`rewardName${i + 1}`] = reward?.rewardName ; 
  //         row[`player${i + 1}`] = reward?.player ; 
  //         row[`createdDate${i + 1}`] = reward?.createdDate
  //           ? moment(convertGMT8date(reward.createdDate)).format('DD-MM-YYYY')
  //           : '';
  //       }

  //       listRow.push(row);
  //     });

  //     const startCol = 13;
  //     const totalRewardCols = maxRewardsLength * 4;

  //     const rowRewardsHeader = worksheet.getRow(3);
  //     const rowSubHeaders = worksheet.getRow(4);

  //     for (let i = 0; i < maxRewardsLength; i++) {
  //       const colStart = startCol + i * 4;
  //       const colEnd = colStart + 3;

  //       // Merge and style top-level "Rewards X"
  //       worksheet.mergeCells(3, colStart, 3, colEnd);
  //       const cell = rowRewardsHeader.getCell(colStart);
  //       cell.value = maxRewardsLength === 1 ? 'Reward' : `Reward ${i}`;
  //       cell.alignment = { horizontal: 'center', vertical: 'middle' };
  //       cell.fill = {
  //         type: 'pattern',
  //         pattern: 'solid',
  //         fgColor: { argb: 'FCE4D6' },
  //       };

  //       // Sub-headers
  //       rowSubHeaders.getCell(colStart).value = 'Reward Code';
  //       rowSubHeaders.getCell(colStart + 1).value = 'Reward Name';
  //       rowSubHeaders.getCell(colStart + 2).value = 'Claimed by';
  //       rowSubHeaders.getCell(colStart + 3).value = 'Claimed Date';

  //       rowSubHeaders.getCell(colStart).alignment =
  //         rowSubHeaders.getCell(colStart + 1).alignment =
  //         rowSubHeaders.getCell(colStart + 2).alignment =
  //           { horizontal: 'center', vertical: 'middle' };

  //       columns.push({ key: `rewardCode${i + 1}`, width: 30 });
  //       columns.push({ key: `rewardName${i + 1}`, width: 30 });
  //       columns.push({ key: `player${i + 1}`, width: 30 });
  //       columns.push({ key: `createdDate${i + 1}`, width: 30 });
  //     }
  //     headers.forEach((header, index) => {
  //       worksheet.getRow(4).getCell(index + 1).value = header;
  //     });

  //     worksheet.columns = columns;
  //     worksheet.getRow(4).alignment = { vertical: 'bottom', horizontal: 'center' };

  //     for (let i = startCol; i < startCol + totalRewardCols; i++) {
  //       worksheet.getRow(4).getCell(i).fill = {
  //         type: 'pattern',
  //         pattern: 'solid',
  //         fgColor: { argb: 'FCE4D6' },
  //       };
  //     }

  //     for (let i = 1; i <= 12; i++) {
  //       worksheet.getRow(4).getCell(i).fill = {
  //         type: 'pattern',
  //         pattern: 'solid',
  //         fgColor: { argb: 'BDD7EE' },
  //       };
  //     }

  //     listRow.forEach(row => worksheet.addRow(row));
  //   }

  //   return workbook;
  // },

  
};

export default generateReport;
